import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/**
 * One stored snapshot's metadata.
 *
 * The captured content itself never travels as JSON to the client — it is replayed as the same SSE
 * frame sequence a live load emits, from the snapshot's own stream route.
 */
export type AppSnapshot = {
  id: number;
  app_id: number;
  name: string;
  byte_size: number;
  created_at: string;
};

export function parseAppSnapshot(value: unknown): AppSnapshot | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const row = value as {
    id?: unknown;
    app_id?: unknown;
    name?: unknown;
    byte_size?: unknown;
    created_at?: unknown;
  };
  if (typeof row.id !== "number" || typeof row.name !== "string") {
    return null;
  }
  return {
    id: row.id,
    app_id: typeof row.app_id === "number" ? row.app_id : 0,
    name: row.name,
    byte_size: typeof row.byte_size === "number" ? row.byte_size : 0,
    created_at: typeof row.created_at === "string" ? row.created_at : "",
  };
}

export function parseAppSnapshots(value: unknown): AppSnapshot[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const snapshots: AppSnapshot[] = [];
  for (const raw of value) {
    const parsed = parseAppSnapshot(raw);
    if (parsed !== null) {
      snapshots.push(parsed);
    }
  }
  return snapshots;
}

/**
 * A snapshot as the builder sees it: the summary plus how many live share links are pinned to it.
 *
 * Only the builder's own list carries the count. The published picker and the public shared-link
 * response deliberately omit it — it is share-link information an anonymous viewer has no business
 * seeing.
 */
export type AppSnapshotWithShareUsage = AppSnapshot & { pinnedShareCount: number };

function parseSnapshotWithShareUsage(value: unknown): AppSnapshotWithShareUsage | null {
  const snapshot = parseAppSnapshot(value);
  if (snapshot === null) {
    return null;
  }
  const count = (value as { pinned_share_count?: unknown }).pinned_share_count;
  // Defaulting to 0 rather than rejecting the row: a missing count only costs the delete dialog its
  // warning, whereas dropping the row would hide a snapshot the author is trying to manage.
  return { ...snapshot, pinnedShareCount: typeof count === "number" ? count : 0 };
}

/**
 * Capture the app as the published view renders it, stored under `name`.
 *
 * Slow by nature — it runs every widget's query before it returns, so callers must keep their saving
 * state up for the whole request rather than assuming a quick round trip. A duplicate name comes back
 * as a 409 whose message is meant to be shown in the dialog.
 */
export async function captureAppSnapshot(appId: number, name: string): Promise<AppSnapshot> {
  const res = await apiFetch(`/v1/apps/${appId}/snapshots`, {
    method: "POST",
    // Required: apiFetch sets only the tenant and auth headers, so without this the browser sends a
    // string body as text/plain and the endpoint rejects it before schema validation.
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const parsed = parseAppSnapshot(await res.json());
  if (parsed === null) {
    throw new Error("Unexpected snapshot response.");
  }
  return parsed;
}

/**
 * The builder's snapshot list, newest first, each row carrying its pinned share-link count.
 *
 * Distinct from `listPublishedAppSnapshots`, which serves the view's picker and has no count.
 */
export async function listAppSnapshots(appId: number): Promise<AppSnapshotWithShareUsage[]> {
  const res = await apiFetch(`/v1/apps/${appId}/snapshots`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const raw: unknown = await res.json();
  if (!Array.isArray(raw)) {
    return [];
  }
  const rows: AppSnapshotWithShareUsage[] = [];
  for (const value of raw) {
    const parsed = parseSnapshotWithShareUsage(value);
    if (parsed !== null) {
      rows.push(parsed);
    }
  }
  return rows;
}

/**
 * Rename a snapshot. A name the app already uses comes back as a 409 whose message belongs in the
 * dialog, the same way a duplicate capture name does.
 */
export async function renameAppSnapshot(
  appId: number,
  snapshotId: number,
  name: string,
): Promise<AppSnapshot> {
  const res = await apiFetch(`/v1/apps/${appId}/snapshots/${snapshotId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const parsed = parseAppSnapshot(await res.json());
  if (parsed === null) {
    throw new Error("Unexpected snapshot response.");
  }
  return parsed;
}

/**
 * Delete a snapshot and its stored document. Returns nothing — the route answers 204.
 *
 * Irreversible, and a share link pinned to this snapshot stops working, so callers are expected to
 * have confirmed with the author first.
 */
export async function deleteAppSnapshot(appId: number, snapshotId: number): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/snapshots/${snapshotId}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
